import { Box } from '@mui/material';
import { useMemo } from 'react';
import { Table, useOnScrollDown } from 'redesign';
import { useTranslation } from 'react-i18next';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { GetDocumentsResponse } from 'hq/services/documents-service.types';
import { RenderUnsettledUI } from 'shared/components';
import { DocumentsColumnDefinition, DocumentsTableDefinition } from './documents-table.types';
import { getDocumentsColumnDefinition } from './utils/get-documents-column-definition';
import { useGetDocumentItemActions } from './hooks/use-get-document-item-actions/use-get-document-item-actions';
import { useGetDocuments } from './hooks/use-get-documents';
import { getDocumentTableData } from './utils/get-document-table-data';
import { DocumentSignDialog } from '../dialogs/document-sign-dialog/document-sign-dialog';
import { DocumentsParams, SetDocumentParams } from '../documents-type';

type DocumentsTableProps = {
  documentsQuery: InfiniteQueryObserverSuccessResult<GetDocumentsResponse, unknown>;
  params: DocumentsParams;
  setParams: SetDocumentParams;
};

export const DocumentsTableProvider = ({
  params,
  setParams,
}: {
  params: DocumentsParams;
  setParams: SetDocumentParams;
}) => {
  const documentsQuery = useGetDocuments(params);

  if (documentsQuery.status !== 'success') {
    return <RenderUnsettledUI data={documentsQuery} />;
  }

  return <DocumentsTable documentsQuery={documentsQuery} params={params} setParams={setParams} />;
};

export const DocumentsTable = ({ documentsQuery, params, setParams }: DocumentsTableProps) => {
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);
  const columns = useMemo(() => getDocumentsColumnDefinition(t), []);
  const { data } = documentsQuery;
  const flatData = useMemo(() => data.pages.flatMap((page) => page.items), [data]);
  const tableData = useMemo(() => getDocumentTableData(flatData), [flatData]);
  const { getRowActions, isOpen, handleCloseDialog } = useGetDocumentItemActions({
    setParams,
    params,
  });

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: documentsQuery,
    flatData: flatData,
  });

  return (
    <Box height="90%">
      <Table<DocumentsColumnDefinition, DocumentsTableDefinition>
        stickyHeader
        columns={columns}
        data={tableData}
        getRowActions={getRowActions}
        data-testid="documents-virtual-table"
        onScroll={fetchIfBottomScrolled}
      />
      {params.documentId ? (
        <DocumentSignDialog
          isOpen={isOpen}
          onClose={handleCloseDialog}
          documentId={params.documentId}
        />
      ) : null}
    </Box>
  );
};
